import clsx from 'clsx'
import renderInputField from 'components/FormControls/textInput'
import { Button, HelperText, Icon, Spinner, Tooltip } from 'gu-ui-kit'
import omit from 'lodash/omit'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Field, formValueSelector, reduxForm } from 'redux-form'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import emailValidator from 'utils/validators/emailValidator'
import * as Yup from 'yup'
import initialAdvertiserInfo from 'modules/Forms/Account/Create/initialAdvertiserInfo'
import advertiserInfoValidationRulesExtended from 'modules/Forms/Account/Create/subcomponents/advertiser-info-validation-rules-extended'
import LoginField from '../subcomponents/LoginField'
import { useAccountCreateFormSubmit } from 'modules/Forms/Account/Create/useAccountCreateFormSubmit'
import SubmitButtons from 'modules/Forms/Account/Create/subcomponents/submitButtons'
import AdvertiserInfoGroup from 'modules/Forms/Account/Create/subcomponents/AdvertiserInfoGroup'
import formStyles from 'modules/Forms/common-form.module.scss'
import renderSelect from 'components/FormControls/select'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import YANDEX_API_V1 from 'api/yandex/v1'
import baseLoginValidator from 'utils/validators/baseLoginValidator'
import { FETCH_YANDEX_USERS, SET_YANDEX_USERS } from 'modules/Services/types'

export const YandexBusinessSchema = (networkType) =>
  Yup.object()
    .shape({
      logins: Yup.array().of(
        Yup.object().shape({
          login: Yup.string()
            .test('baseLoginTest', '', (value, ctx) =>
              baseLoginValidator(
                value,
                ctx,
                'services::forms.register_account.validation_messages.login.yandex_incorrect_format',
              ),
            )
            .max(
              30,
              'services::forms.register_account.validation_messages.login.yandex_incorrect_format',
            )
            .required('common::validation_messages.required'),
        }),
      ),
      login: Yup.string().when('logins', ([logins], schema) =>
        logins
          ? schema.strip()
          : schema
              .test('baseLoginTest', '', (value, ctx) =>
                baseLoginValidator(
                  value,
                  ctx,
                  'services::forms.register_account.validation_messages.login.yandex_incorrect_format',
                ),
              )
              .max(
                30,
                'services::forms.register_account.validation_messages.login.yandex_incorrect_format',
              )
              .required('common::validation_messages.required'),
      ),
      network: Yup.string().required('common::validation_messages.required'),
      yandex_user_id: Yup.string().required('common::validation_messages.required'),
      email: Yup.string()
        .test(
          'emailValidator',
          'common::validation_messages.incorrect_email_format',
          emailValidator,
        )
        .required('common::validation_messages.required'),
      lastname: Yup.string().required('common::validation_messages.required'),
      firstname: Yup.string().required('common::validation_messages.required'),
    })
    .concat(advertiserInfoValidationRulesExtended(networkType))

let CreateYandexBusinessAccountForm = ({
  handleSubmit,
  submitting,
  onClose,
  autofill,
  networkType,
  formHeader,
  networkId,
  form,
  initialValues,
  change,
  array,
  multiLogins,
}) => {
  let childWindow

  const selector = formValueSelector(form)
  const { t } = useTranslation()
  const breakpoint = useBreakpoint()
  const submitAccountData = useAccountCreateFormSubmit(form, onClose, multiLogins)
  const dispatch = useDispatch()
  const [initialUserIdSelected, setInitialUserIdSelected] = useState(false)

  const {
    formValues: { email, advertiser_info, yandex_user_id },
    user,
    accountCreate: {
      yandexUsers: { data: yandexUsersData = [], loading: yandexUsersLoading = false },
    } = {},
  } = useSelector((state) => ({
    formValues: selector(state, 'email', 'advertiser_info', 'yandex_user_id'),
    user: state['app'].user,
    accountCreate: state['services/account-create'],
  }))

  useEffect(() => {
    user.name && email === initialValues.email && autofill('email', user.name)
  }, [user])

  useEffect(() => {
    dispatch({ type: FETCH_YANDEX_USERS })
  }, [])

  const submit = useCallback(
    (values) => submitAccountData(omit(values, ['advertiser_template'])),
    [submitAccountData],
  )

  global.getBack = useCallback(
    ({ id = null, login = null }) => {
      !!childWindow && childWindow.close()
      childWindow = null
      if (id) {
        autofill('yandex_user_id', id)
        dispatch({
          type: SET_YANDEX_USERS,
          payload: { data: [...yandexUsersData, { id, login }] },
        })
        setInitialUserIdSelected(true)
      }
    },
    [childWindow, autofill, yandexUsersData],
  )

  const openConnectWindow = useCallback(() => {
    childWindow = global.open(
      '',
      'Yandex oAuth dialog',
      'resizable,toolbar=yes,location=yes,scrollbars=yes,menubar=yes,width=800,height=600,top=0,left=0',
    )

    YANDEX_API_V1.getNetworkAuthUrl()
      .then(({ response: { data = {} } = {} }) => {
        if (!childWindow) {
          return
        }
        const { url = null } = data
        if (url) {
          childWindow.location = url
          childWindow.focus()
        } else {
          childWindow.close()
        }
      })
      .catch(() => {
        childWindow && childWindow.close()
      })
  }, [childWindow, networkType])

  const yandexUsersOptions = useMemo(
    () => yandexUsersData?.map(({ id, login }) => ({ label: login, value: id })) ?? [],
    [yandexUsersData],
  )

  useEffect(() => {
    if (!initialUserIdSelected && yandexUsersData?.length && !yandex_user_id) {
      autofill('yandex_user_id', yandexUsersData?.[0]?.id ?? null)
      setInitialUserIdSelected(true)
    }
  }, [yandexUsersData, yandex_user_id, initialUserIdSelected])

  return (
    <>
      {formHeader}

      <form
        onSubmit={handleSubmit(submit)}
        noValidate
      >
        <Field
          disabled
          name="network"
          hidden
          fullWidth
          component={renderInputField}
          t={t}
        />

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <LoginField
            form={form}
            autofill={autofill}
            multiLogins={multiLogins}
            array={array}
            networkId={networkId}
            label={'services::forms.register_account.yandex.fields.login.label'}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t('services::forms.register_account.yandex.fields.login.tooltip')}
              placement="top"
              arrow
              tooltipClassName={formStyles['tooltip']}
              textAlign={'center'}
            >
              <Icon
                name={'information'}
                color={'secondary-grey'}
                size={'m'}
              />
            </Tooltip>
          </div>
        </div>

        <div className={'margin-bottom-24'}>
          <div className={formStyles['row_with-tooltip']}>
            <Field
              fullWidth
              name="yandex_user_id"
              normalize={(value) => value}
              component={renderSelect}
              emptyOptionLabel={t('common::select_empty_option')}
              clearOptionLabel={t('common::select_clear_option')}
              label={t('services::forms.register_account.yandex.fields.yandex_user_id.label')}
              t={t}
              options={yandexUsersOptions}
              ControlProps={{
                startAdornment: yandexUsersLoading ? (
                  <Spinner
                    width={16}
                    color={'Label-GU-Primary'}
                  />
                ) : null,
                endAdornment: !breakpoint.sm && (
                  <Button
                    link
                    variant={'ghost'}
                    size={'m'}
                    onClick={openConnectWindow}
                    startIcon={<Icon name={'plus'} />}
                  >
                    {t(
                      'services::forms.register_account.yandex.fields.yandex_user_id.get_url_label.label',
                    )}
                  </Button>
                ),
              }}
            />

            <div className={formStyles['tooltip-container']}>
              <Tooltip
                title={t('services::forms.register_account.yandex.fields.yandex_user_id.tooltip')}
                placement="top"
                arrow
                tooltipClassName={formStyles['tooltip']}
                textAlign={'center'}
              >
                <Icon
                  name={'information'}
                  color={'secondary-grey'}
                  size={'m'}
                />
              </Tooltip>
            </div>
          </div>

          {breakpoint.sm && (
            <HelperText>
              <Button
                link
                variant={'ghost'}
                size={'s'}
                onClick={openConnectWindow}
                startIcon={<Icon name={'plus'} />}
              >
                {t(
                  'services::forms.register_account.yandex.fields.yandex_user_id.get_url_label.label',
                )}
              </Button>
            </HelperText>
          )}
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="email"
            type="email"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.yandex.fields.email.label')}
            t={t}
          />

          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t('services::forms.register_account.yandex.fields.email.tooltip')}
              placement="top"
              arrow
              tooltipClassName={formStyles['tooltip']}
              textAlign={'center'}
            >
              <Icon
                name={'information'}
                color={'secondary-grey'}
                size={'m'}
              />
            </Tooltip>
          </div>
        </div>

        <div className={clsx('margin-bottom-16')}>
          <Field
            name="firstname"
            type="text"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.yandex.fields.firstname.label')}
            t={t}
          />
        </div>

        <div className={clsx('margin-bottom-16')}>
          <Field
            name="lastname"
            type="text"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.yandex.fields.lastname.label')}
            t={t}
          />
        </div>

        <AdvertiserInfoGroup
          form={form}
          change={change}
          advertiser_info={advertiser_info}
          networkType={networkType}
          initialValues={initialValues}
        />

        <SubmitButtons
          submitted={submitAccountData.submitted}
          submitting={submitting}
          onClose={onClose}
        />
      </form>
    </>
  )
}

CreateYandexBusinessAccountForm = reduxForm({
  form: 'CreateYandexBusinessAccountForm',
  initialValues: {
    login: '',
    network: '',
    email: '',
    lastname: '',
    firstname: '',
    yandex_user_id: undefined,
    payment_profile_id: undefined,
    advertiser_info: { ...initialAdvertiserInfo, okved_code: null },
  },
  shouldAsyncValidate: shouldAsyncValidate,
})(CreateYandexBusinessAccountForm)

CreateYandexBusinessAccountForm.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  onClose: PropTypes.func,
  autofill: PropTypes.func,
  formHeader: PropTypes.element,
  change: PropTypes.func,
  networkId: PropTypes.string,
  networkType: PropTypes.string,
  initialValues: PropTypes.object,
  form: PropTypes.string,
  multiLogins: PropTypes.bool,
  array: PropTypes.object,
}

export default CreateYandexBusinessAccountForm
